import {
  Autocomplete,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import Icon from 'components/Icon'
import TestTeamExternalIntegration from 'components/rbac-settings/teams/TestTeamExternalIntegration'
import { URLHelperText } from 'components/services/create/URLHelperText'
import { StatusCheckSuccessEvaluation } from 'components/status-checks/form/status-check/StatusCheckSuccessEvaluation'
import {
  buildTeamExternalIntegration,
  getExistingStatusCheckByMonitorUrl,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'

const DEFAULT_CONTENT_TYPE_OPTIONS = [
  'application/json',
  'text/plain',
  'text/xml'
]

const fieldsToWatch = [
  'endpointConfiguration',
  'monitorUrl',
  'observabilityTool',
  'externalIntegration'
] as const

/**
 * Renders help text which links to our docs on request body interpolation values.
 */
const RequestPayloadHelperText: React.FC = () => {
  return (
    <Typography color="textSecondary" variant="caption">
      Request bodies can contain{' '}
      <MUIExternalLink
        href="https://www.gremlin.com/docs/platform-health-checks-custom"
        sx={{ alignItems: 'center', columnGap: 0.5, display: 'inline-flex' }}
      >
        Interpolation Values <Icon icon="arrowUpRightFromSquare" />
      </MUIExternalLink>
    </Typography>
  )
}

export const StatusCheckNameMonitorSuccessEvaluation: React.FC = () => {
  const { postViaUi: hasPostMethodForStatusChecks } = useFeatureFlags()
  const { control, formState, getFieldState, getValues, register, trigger } =
    useFormContext<StatusCheckFormValues>()

  const [
    endpointConfiguration,
    monitorUrl,
    observabilityTool,
    externalIntegration
  ] = useWatch<StatusCheckFormValues, typeof fieldsToWatch>({
    control,
    name: fieldsToWatch
  })

  const endpointConfigurationField = getFieldState('endpointConfiguration')
  const monitorUrlField = getFieldState('monitorUrl')
  const statusChecksUrls = getStatusChecksUrls()
  const isAWS = observabilityTool.type === ObservabilityToolType.AWS
  const isCustomObservabilityTool =
    observabilityTool.type === ObservabilityToolType.CUSTOM
  const shouldShowMethodField =
    hasPostMethodForStatusChecks && isCustomObservabilityTool
  const isPostMethod = endpointConfiguration?.method === 'POST'

  const {
    handleSuccess,
    handleTestConnectionError,
    isTestStatusCheckDisabled,
    setEndpointResponse,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  } = useStatusCheckDetailsContext()

  const { statusChecks: existingStatusChecks } = useStatusChecksContext()
  const existingStatusCheck = useMemo(() => {
    return getExistingStatusCheckByMonitorUrl(
      existingStatusChecks,
      observabilityTool,
      monitorUrl
    )
  }, [existingStatusChecks, monitorUrl, observabilityTool])

  useEffect(() => {
    if (!endpointConfigurationField.isDirty && !monitorUrlField.isDirty) return

    setIsStatusCheckConnectionValidated(false)
    setIsStatusCheckEvaluationValidated(false)
  }, [
    endpointConfigurationField.isDirty,
    monitorUrlField.isDirty,
    setEndpointResponse,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  ])

  return (
    <Stack spacing={3}>
      <FormControl>
        <Typography mb={2}>What is the name of this Health Check?</Typography>
        <TextField
          data-testid="status-check-monitor-name-input"
          error={Boolean(formState.errors.statusCheckName)}
          helperText={formState.errors.statusCheckName?.message}
          label="Health Check Name"
          variant="outlined"
          {...register('statusCheckName')}
        />
      </FormControl>
      <Stack spacing={1} sx={{ width: '100%' }}>
        <Typography mb={2}>
          What monitor or alert will help Gremlin determine the health of your
          Service?
        </Typography>
        <Stack direction="row" spacing={1}>
          {shouldShowMethodField && (
            <FormControl>
              <Controller
                control={control}
                name="endpointConfiguration.method"
                render={({ field }) => {
                  const { method: methodError } =
                    formState.errors.endpointConfiguration ?? {}

                  return (
                    <TextField
                      {...field}
                      select
                      data-testid="status-check-monitor-method-select"
                      error={Boolean(methodError)}
                      helperText={methodError?.message}
                      label="method"
                      sx={{ minWidth: 120 }}
                      onChange={(event) => {
                        const { value } = event.target

                        if (monitorUrl) {
                          trigger('monitorUrl')
                        }

                        field.onChange(value)
                      }}
                    >
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                    </TextField>
                  )
                }}
              />
            </FormControl>
          )}
          <FormControl sx={{ flexGrow: 1 }}>
            <Controller
              control={control}
              name="monitorUrl"
              render={({ field }) => (
                <TextField
                  {...field}
                  aria-describedby="monitor-url-helper-text"
                  data-testid="status-check-monitor-url-input"
                  error={Boolean(formState.errors.monitorUrl)}
                  helperText={formState.errors.monitorUrl?.message}
                  label="Monitor or Alert URL"
                  variant="outlined"
                />
              )}
            />
            {formState.errors.monitorUrl?.type === 'notOneOf' && (
              <FormHelperText id="link-to-existing-health-check">
                <Typography variant="caption">
                  <RouterLink
                    to={statusChecksUrls.edit(existingStatusCheck?.id)}
                  >
                    Go to existing Health Check
                  </RouterLink>
                </Typography>
              </FormHelperText>
            )}
            <FormHelperText id="monitor-url-helper-text">
              <URLHelperText integration={externalIntegration} />
            </FormHelperText>
          </FormControl>
        </Stack>
        {shouldShowMethodField && isPostMethod && (
          <>
            <FormControl>
              <Controller
                control={control}
                name="endpointConfiguration.headers.content-type"
                render={({ field }) => {
                  return (
                    <Autocomplete<string, false, false, true>
                      autoComplete
                      freeSolo
                      options={DEFAULT_CONTENT_TYPE_OPTIONS}
                      renderInput={(params) => {
                        const { headers: headersError } =
                          formState.errors.endpointConfiguration ?? {}

                        return (
                          <TextField
                            {...field}
                            {...params}
                            data-testid="status-check-monitor-request-content-type-input"
                            error={Boolean(headersError?.['content-type'])}
                            helperText={headersError?.['content-type']?.message}
                            inputProps={{ ...params.inputProps, maxLength: 50 }}
                            label="Content Type"
                            name="endpointConfiguration.headers.content-type"
                            variant="outlined"
                          />
                        )
                      }}
                      value={field.value ?? ''}
                      onChange={(_, value) => {
                        field.onChange(value)
                      }}
                    />
                  )
                }}
              />
            </FormControl>
            <FormControl>
              <Controller
                control={control}
                name="endpointConfiguration.payload"
                render={({ field }) => {
                  const { payload: payloadError } =
                    formState.errors.endpointConfiguration ?? {}

                  return (
                    <TextField
                      {...field}
                      multiline
                      data-testid="status-check-monitor-request-body-input"
                      error={Boolean(payloadError)}
                      helperText={
                        payloadError?.message ?? <RequestPayloadHelperText />
                      }
                      label="Request Body"
                      maxRows={10}
                      minRows={4}
                      variant="outlined"
                    />
                  )
                }}
              />
            </FormControl>
          </>
        )}
        {(isCustomObservabilityTool || isAWS) && (
          <Stack mt={2} spacing={3}>
            <TestTeamExternalIntegration
              authenticate={false}
              buttonText="Test Connection"
              disabled={isTestStatusCheckDisabled}
              endpointConfiguration={endpointConfiguration}
              integration={buildTeamExternalIntegration(getValues())}
              onError={handleTestConnectionError}
              onResponseReceived={handleSuccess}
            />
            <StatusCheckSuccessEvaluation />
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
